import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Package, Calendar, Lock, Gift } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import LotReturnCalculator from "../../components/LotReturnCalculator";

export default function CustomerLotDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lot, setLot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [credit, setCredit] = useState(0);

  useEffect(() => {
    api.get(`/lots/${id}`).then((r) => setLot(r.data));
    api.get("/customer/referrals").then((r) => setCredit(r.data?.referral_credit || 0)).catch(() => {});
  }, [id]);

  const loadRzp = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const purchase = async () => {
    if (user?.kyc_status !== "approved") {
      toast.error("Complete KYC first");
      return navigate("/kyc");
    }
    setLoading(true);
    try {
      const init = await api.post("/customer/purchase/initiate", { lot_id: lot.id });
      await loadRzp();

      // If real Razorpay key available, open checkout, otherwise complete with mock
      if (window.Razorpay && init.data.razorpay_key_id && !init.data.razorpay_order_id.startsWith("order_mock_")) {
        const options = {
          key: init.data.razorpay_key_id,
          amount: init.data.amount,
          currency: init.data.currency,
          order_id: init.data.razorpay_order_id,
          name: "PassiveIearn",
          description: init.data.lot_name,
          prefill: {
            name: init.data.customer.name || "",
            contact: init.data.customer.phone || "",
            email: init.data.customer.email || "",
          },
          theme: { color: "#D71921" },
          handler: async (resp) => {
            await api.post("/customer/purchase/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              lot_id: lot.id,
            });
            toast.success("Purchase successful!");
            navigate("/portfolio");
          },
          modal: { ondismiss: () => setLoading(false) },
        };
        new window.Razorpay(options).open();
      } else {
        // Mock flow (test environment)
        await api.post("/customer/purchase/verify", {
          razorpay_order_id: init.data.razorpay_order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "mock-signature",
          lot_id: lot.id,
        });
        toast.success("Purchase successful! (test mode)");
        navigate("/portfolio");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
      setLoading(false);
    }
  };

  if (!lot) return <div className="text-center text-[#6B5F58] py-12">Loading…</div>;

  return (
    <div>
      <Link to="/lots" className="inline-flex items-center gap-1 text-sm text-[#6B5F58] hover:text-[#1F1816] mb-4">
        <ArrowLeft className="w-4 h-4" /> All lots
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden border border-[#E8E0D2] aspect-[16/9] mb-6">
            <img alt={lot.name}
              src="https://images.pexels.com/photos/36744654/pexels-photo-36744654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              className="w-full h-full object-cover" />
          </div>
          <div className="text-xs uppercase tracking-widest text-[#6B5F58]">{lot.lot_code}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">{lot.name}</h1>
          <p className="text-[#6B5F58] mt-3 leading-relaxed">{lot.description}</p>

          <div className="grid grid-cols-3 gap-4 mt-6">
            <Stat icon={Package} label="Animals" value={lot.animal_count} />
            <Stat icon={Lock} label="Lock-in" value={`${lot.lock_period_days}d`} />
            <Stat icon={Calendar} label="Sale Window" value={`${lot.expected_sale_months}mo`} />
          </div>

          {lot.animals?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-display text-xl mb-3">Animals in this lot</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {lot.animals.slice(0, 8).map((a) => (
                  <div key={a.id} className="gf-card p-4 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{a.tag_number}</div>
                      <div className="text-xs text-[#6B5F58]">
                        {a.species} · {a.breed} · {a.age_months}mo · {a.current_weight_kg}kg
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-[#FCEDED] text-[#D71921]">{a.health_status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="gf-card p-6 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-[#6B5F58]">Price</div>
            <div className="font-display text-4xl text-[#D71921] mt-1">₹{lot.price_per_lot.toLocaleString()}</div>
            <div className="text-xs text-[#6B5F58] mt-1">Includes purchase &amp; care of {lot.animal_count} animals</div>

            <div className="my-6 border-t border-[#E8E0D2]" />

            <ul className="text-sm text-[#1F1816] space-y-2.5">
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-[#3F6B3F] mt-0.5" /> 100% transparent tracking</li>
              <li className="flex items-start gap-2"><Calendar className="w-4 h-4 text-[#3F6B3F] mt-0.5" /> {lot.lock_period_days}-day minimum lock-in</li>
              <li className="flex items-start gap-2"><Package className="w-4 h-4 text-[#3F6B3F] mt-0.5" /> Each animal gets unique QR ID</li>
            </ul>

            {credit > 0 && (
              <div className="mt-4 rounded-xl p-3 border" data-testid="lot-credit-banner"
                style={{ background: "linear-gradient(90deg, #E6F0E5 0%, #FFF5DA 100%)", borderColor: "#3F6B3F" }}>
                <div className="flex items-center gap-2 text-[#3F6B3F] text-xs font-bold uppercase tracking-wider">
                  <Gift className="w-3.5 h-3.5" /> Referral credit applied
                </div>
                <div className="text-sm mt-1">
                  <span className="line-through text-[#6B5F58]">₹{lot.price_per_lot.toLocaleString()}</span>
                  {" "}<b className="text-[#3F6B3F]">−₹{Math.min(credit, lot.price_per_lot).toLocaleString()}</b>
                  {" "}= <b className="text-[#D71921]">₹{Math.max(0, lot.price_per_lot - credit).toLocaleString()}</b>
                </div>
              </div>
            )}

            <button data-testid="buy-lot-btn" onClick={purchase} disabled={loading || lot.status !== "available"}
              className="w-full mt-6 px-6 py-3.5 rounded-xl bg-[#D71921] text-white font-medium hover:bg-[#B0151E] transition-all disabled:opacity-60">
              {loading ? "Processing..." : lot.status !== "available" ? "Sold out" : "Purchase Lot"}
            </button>
            {user?.kyc_status !== "approved" && (
              <div className="mt-3 text-xs text-amber-700 text-center">KYC required to purchase</div>
            )}
          </div>

          <div className="mt-6">
            <LotReturnCalculator lotPrice={lot.price_per_lot} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="gf-card p-4">
      <Icon className="w-4 h-4 text-[#3F6B3F]" />
      <div className="text-xs text-[#6B5F58] mt-2 uppercase tracking-wider">{label}</div>
      <div className="font-display text-xl mt-1">{value}</div>
    </div>
  );
}
